import {ProductsSectionTagsOrders, ProductTag} from '@/libs/constants/common.constants';
import {generateStars} from '@/libs/rating';
import {parsePhoneNumber} from '@/libs/string.util';
import {randomIntFromInterval} from '@/libs/number.util';
import {getActualPricePolicy, parseCartProducts} from '@/libs/product';

const errorData = { error: { errors: 'INVALID_TAKEN_DATA', status: 503 } };

export function fetchCommonConfigCallback(result, currency) {
	if (result.currencies.length === 0 || !result.contact) return errorData;
	result.contact.parsedTelNumber = parsePhoneNumber(result.contact.telNumber);
	result.currentCurrency = result.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result
		}
	}
}

export function fetchHomeCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	parseProductItems(result.getPublicStoreBannerProducts, (item) => {
		item.titleRandomNum = randomIntFromInterval(1, 5);
	});
	
	let productCountWithoutSale = 0;
	for (let i = 0; i < ProductsSectionTagsOrders.length; i++) {
		if (ProductsSectionTagsOrders[i] !== 'sale') {
			productCountWithoutSale += result.getPublicHomeProducts[ProductsSectionTagsOrders[i]].length;
		}
		
		parseProductItems(result.getPublicHomeProducts[ProductsSectionTagsOrders[i]], (item) => {
			if (item.reviewComputed) {
				item.reviewComputed.stars = generateStars(item.reviewComputed.average);
			}
		});
	}
	result.getPublicHomeProducts.count = productCountWithoutSale;
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			categories: result.getPublicStoreProductCategories,
			brands: result.getPublicStoreProductBrands,
			brandImages: result.getPublicStoreProductBrandsWithImage,
			bannerProducts: result.getPublicStoreBannerProducts,
			bannerCoupons: result.getPublicHomeBannerCoupons,
			features: result.getPublicStoreHomeFeatures,
			products: result.getPublicHomeProducts,
			blogs: result.getPublicHomeBlogs,
			client: result.getClientSelfNullable
		}
	}
}

export function fetchCatalogCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	parseProductItems(result.getPublicProductsPaginated.items, (item) => {
		if (item.reviewComputed) {
			item.reviewComputed.stars = generateStars(item.reviewComputed.average);
		}
	});

	return {
		data: {
			common: result.getStoreCommonConfig,
			products: result.getPublicProductsPaginated.items ?? [],
			productOptions: {
				hasMore: result.getPublicProductsPaginated.hasMore ?? false,
				total: result.getPublicProductsPaginated.total ?? 0
			},
			categories: result.getPublicStoreProductCategories,
			brands: result.getPublicStoreProductBrands
		}
	}
}

export function fetchProductCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	if (result.getPublicProduct.prices.length !== 1) {
		return errorData;
	}
	
	if (result.getPublicProduct.discount > 0 && result.getPublicProduct.tag === ProductTag.SALE) {
		result.getPublicProduct.prices[0].priceComputed = result.getPublicProduct.prices[0].price - result.getPublicProduct.prices[0].price * result.getPublicProduct.discount;
		result.getPublicProduct.prices[0].overpriceComputed = result.getPublicProduct.prices[0].price;
		result.getPublicProduct.prices[0].crossed = true;
	} else {
		result.getPublicProduct.prices[0].priceComputed = result.getPublicProduct.prices[0].price;
		result.getPublicProduct.prices[0].overpriceComputed = result.getPublicProduct.prices[0].overprice;
	}
	result.getPublicProduct.price = result.getPublicProduct.prices[0];
	if (result.getPublicProduct.reviewComputed) {
		result.getPublicProduct.reviewComputed.stars = generateStars(result.getPublicProduct.reviewComputed.average);
	}
	
	parseProductItems(result.getPublicRelatedProducts, (item) => {
		if (item.reviewComputed) {
			item.reviewComputed.stars = generateStars(item.reviewComputed.average);
		}
	});
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			product: result.getPublicProduct,
			relatedProducts: result.getPublicRelatedProducts,
			client: result.getClientSelfNullable
		}
	}
}

export function parseProductItems(items, cb) {
	for (let j = 0; j < items.length; j++) {
		if (items[j].prices.length !== 1) {
			return errorData;
		}
		
		if (items[j].discount > 0 && items[j].tag === ProductTag.SALE) {
			items[j].prices[0].priceComputed = items[j].prices[0].price - items[j].prices[0].price * items[j].discount;
			items[j].prices[0].overpriceComputed = items[j].prices[0].price;
			items[j].prices[0].crossed = true;
		} else {
			items[j].prices[0].priceComputed = items[j].prices[0].price;
			items[j].prices[0].overpriceComputed = items[j].prices[0].overprice;
		}
		items[j].price = items[j].prices[0];
		
		if (typeof cb === 'function') {
			cb(items[j]);
		}
	}
}

export function fetchBlogsCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			blogs: result.getPublicBlogPaginated.items ?? [],
			blogOptions: {
				hasMore: result.getPublicBlogPaginated.hasMore ?? false,
				total: result.getPublicBlogPaginated.total ?? 0
			},
		}
	}
}

export function fetchBlogCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	if (result.getPublicBlog.tags != null) {
		result.getPublicBlog.computedTags = result.getPublicBlog.tags.split(',');
	}
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			blog: result.getPublicBlog,
			relatedBlogs: result.getPublicRelatedBlogs
		}
	}
}

export function fetchFaqCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			faqList: result.getPublicFaqList,
			funFacts: result.getPublicFunFacts,
		}
	}
}

export function fetchContactCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	const telNumbers = [],
			whatsapps = [],
			telegrams = [],
			emails = [];
	for (let i = 0; i < result.getPublicContacts.length; i++) {
		const contact = result.getPublicContacts[i];
		telNumbers.push({
			parsedTelNumber: parsePhoneNumber(contact.telNumber),
			telNumber: contact.telNumber
		});
		if (contact.whatsapp) whatsapps.push(contact.whatsapp);
		if (contact.telegram) telegrams.push(contact.telegram);
		if (contact.email) emails.push(contact.email);
	}
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			contacts: {
				telNumbers,
				whatsapps,
				telegrams,
				emails
			}
		}
	}
}

export function fetchClientAccountCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);

	return {
		data: {
			common: result.getStoreCommonConfig,
			client: result.getClientSelf
		}
	}
}

export function fetchClientCheckoutCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	const {freshData, totalPrice} = parseCartProducts(result.getClientCartsSelf, result.getClientSelf.clientType, true);
	const pricePolicy = result.getActualPricePolicyForClient.length > 0 ? getActualPricePolicy(result.getActualPricePolicyForClient, totalPrice.common + totalPrice.sale) : null;
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			client: result.getClientSelf,
			cart: freshData,
			totalPrice: totalPrice,
			autoUseCoupon: result.getAutoUseCoupon,
			pricePolicy,
			branches: result.getBranchesForClient
		}
	}
}

export function fetchCartCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			autoUseCoupon: result.getAutoUseCoupon,
			firstOrderCoupon: result.getFirstOrderCoupon
		}
	}
}

export function fetchWishlistCallback(result, currency) {
	if (result.currencies.length === 0 || !result.contact) return errorData;
	result.contact.parsedTelNumber = parsePhoneNumber(result.contact.telNumber);
	result.currentCurrency = result.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result
		}
	}
}

export function fetchCheckoutCallback(result, currency) {
	if (result.getStoreCommonConfig.currencies.length === 0 || !result.getStoreCommonConfig.contact) return errorData;
	result.getStoreCommonConfig.contact.parsedTelNumber = parsePhoneNumber(result.getStoreCommonConfig.contact.telNumber);
	result.getStoreCommonConfig.currentCurrency = result.getStoreCommonConfig.currencies.find(c => c.title === currency);
	
	return {
		data: {
			common: result.getStoreCommonConfig,
			autoUseCoupon: result.getAutoUseCoupon,
			firstOrderCoupon: result.getFirstOrderCoupon,
			branches: result.getBranchesForClient
		}
	}
}
